import React from 'react';
import { createUseStyles } from 'react-jss';
import { Avatar, Popover, Badge } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { getTimeInWordFormat } from '../../../utils/dateUtils';
import { getColor, getNameAbbreviation } from '../../../utils/avatarUtils';
import lodash from 'lodash';
import { LiaBot } from '../../assets/LiaBot';
import { readNotification } from '../notificationAPIs';
import { UserType } from '../notificationConstants';

const useStyle = createUseStyles({
    avatarTextStyle: {
        display: 'flex',
        alignItems: 'center',
    },
    timeStyle: {
        display: 'flex',
        flexDirection: 'row',
        gap: '8px',
        alignItems: 'center',
    },
    timeText: {
        color: '#999',
        textAlign: 'right',
        fontSize: '10px',
        fontWeight: 400,
    },
});

interface BadgeReadStatusProps {
    id: string;
    notificationId: string;
    notificationStatus: Record<string, boolean>;
    setNotificationStatus: (status: Record<string, boolean>) => void;
    userId: string | null;
    organizationId: string | null;
}

interface MarkAsReadParams {
    notificationHistoryId: string;
    notificationId: string;
    notificationStatus: Record<string, boolean>;
    setNotificationStatus: (status: Record<string, boolean>) => void;
    userId: string | null;
    organizationId: string | null;
}

const markAsUnRead = async (props: MarkAsReadParams) => {
    const {
        notificationHistoryId,
        notificationId,
        notificationStatus,
        setNotificationStatus,
        userId,
        organizationId,
    } = props;

    setNotificationStatus({
        ...notificationStatus,
        [notificationHistoryId]: false,
    });

    if (userId && organizationId) {
        await readNotification({
            notificationHistoryId,
            notificationId,
            isRead: false,
            isReadAll: false,
            userId,
            organisationId: organizationId,
        });
    }
};

const markAsRead = async (props: MarkAsReadParams) => {
    const {
        notificationHistoryId,
        notificationId,
        notificationStatus,
        setNotificationStatus,
        userId,
        organizationId,
    } = props;

    setNotificationStatus({
        ...notificationStatus,
        [notificationHistoryId]: true,
    });

    if (userId && organizationId) {
        await readNotification({
            notificationHistoryId,
            notificationId,
            isRead: true,
            isReadAll: false,
            organisationId: organizationId,
            userId,
        });
    }
};

const BadgeReadStatus = (props: BadgeReadStatusProps) => {
    const {
        id,
        notificationId,
        notificationStatus,
        setNotificationStatus,
        userId,
        organizationId,
    } = props;

    return (
        <Popover
            content={notificationStatus[id] ? 'Mark as unread' : 'Mark as read'}
        >
            {notificationStatus[id] ? (
                <div
                    onClick={(e) => {
                        markAsUnRead({
                            notificationHistoryId: id,
                            notificationId,
                            notificationStatus,
                            setNotificationStatus,
                            userId,
                            organizationId,
                        });
                        e.stopPropagation();
                    }}
                >
                    <Badge status="default" />
                </div>
            ) : (
                <div
                    onClick={(e) => {
                        markAsRead({
                            notificationHistoryId: id,
                            notificationId,
                            notificationStatus,
                            setNotificationStatus,
                            userId,
                            organizationId,
                        });
                        e.stopPropagation();
                    }}
                >
                    <Badge status="processing" color="#1890ff" />
                </div>
            )}
        </Popover>
    );
};

interface TimeAndBadgeComponentProps {
    item: any;
    notificationId: string;
    notificationStatus: Record<string, boolean>;
    setNotificationStatus: (status: Record<string, boolean>) => void;
    userId?: string | null;
    organisationId?: string | null;
}

export const TimeAndBadgeComponent = (props: TimeAndBadgeComponentProps) => {
    const classes = useStyle();
    const {
        item,
        notificationId,
        notificationStatus,
        setNotificationStatus,
        userId = null,
        organisationId = null,
    } = props;

    const time = getTimeInWordFormat(item.updatedAt);

    return (
        <div className={classes.timeStyle}>
            <div className={classes.timeText}>{time && `${time} ago`}</div>
            <BadgeReadStatus
                id={item.id}
                notificationId={notificationId}
                notificationStatus={notificationStatus}
                setNotificationStatus={setNotificationStatus}
                userId={userId}
                organizationId={organisationId}
            />
        </div>
    );
};

interface ProfileAvatarProps {
    name: string;
    sizeAvatar: number;
    sizeText: number;
    userType?: string;
    isRead?: boolean;
}

export const ProfileAvatar = (props: ProfileAvatarProps) => {
    const classes = useStyle();
    const {
        name = 'System', sizeAvatar, sizeText, userType, isRead = false,
    } = props;

    // For worker userType, show InfoCircleOutlined icon
    if (userType === UserType.WORKER) {
        // Use blue for unread, gray for read
        const iconColor = isRead ? '#999' : '#1890FF';
        const bgColor = isRead ? '#F5F5F5' : '#E6F7FF';

        return (
            <Avatar
                size={sizeAvatar}
                style={{
                    top: '-2px',
                    backgroundColor: bgColor,
                    color: iconColor,
                }}
                icon={<InfoCircleOutlined style={{ fontSize: sizeText }} />}
            />
        );
    }

    return (
        <Avatar
            size={sizeAvatar}
            style={{
                top: '-2px',
                backgroundColor:
          name !== 'LIA'
              ? getColor(getNameAbbreviation(name))?.backgroundColor
              : '#E9F7FF',
                color: getColor(getNameAbbreviation(name))?.color,
            }}
        >
            {name === 'LIA' ? (
                <LiaBot />
            ) : (
                <span
                    style={{ fontSize: sizeText }}
                    className={classes.avatarTextStyle}
                >
                    {lodash.upperCase(name[0])}
                    {' '}
                </span>
            )}
        </Avatar>
    );
};
